import { useCallback, useMemo, useRef, useState } from 'react';
import { useT, type MessageKey } from '../lib/i18n';
import {
  CORE_PACKAGE, DIRECTIVE_LISTS, LANES, type DirectiveList, type EdgeKind, type Lane, type TaxGraph, type TaxNode
} from '../lib/taxonomyGraph';
import { Wires, type Wire } from './Wires';
import { KindIcon } from './icons';

/** How many parts of a kind are shown open before the group starts folded. */
const OPEN_UP_TO = 8;

/**
 * Which lanes share a column: the value types under the documents and the directives' two
 * branches under each other, so that four columns hold everything and fit beside the panel.
 */
const COLUMNS: Lane[][] = [['object'], ['event'], ['document', 'value'], ['process', 'view', 'directive']];
/** More than this many lines and their names are not written: they would lie on each other. */
const LABELS_UP_TO = 14;

interface Row { node: TaxNode; depth: number; children: number; open?: boolean }
interface Group { id: string; kind: 'tree' | DirectiveList | 'values'; rows: Row[]; folded: boolean }

/**
 * The taxonomy as a map (docs/en/43 sec.8): a column for each root, one for the value types,
 * and the directives in two - what happens and what is shown. The page grows downwards as the
 * taxonomy grows and scrolls with it; a kind of directive with many parts is folded to its name
 * and count until it is opened, searched in or something in it is chosen.
 *
 * <p>Only the chosen part's lines are drawn - and, lighter, those of the one the pointer is on -
 * so that a taxonomy of three hundred parts reads as one, not as a ball of string. Every line can
 * still be asked for.
 */
export function TaxonomyMap({ graph, selected, onSelect, onOpen, visible, matches, problems, changed, show, allLines, compact }: {
  graph: TaxGraph;
  selected: string | null;
  onSelect: (id: string) => void;
  /** A part opened: double-clicked, to be looked at on its own. */
  onOpen: (id: string) => void;
  visible: (node: TaxNode) => boolean;
  /** What a search finds: the groups they are in open by themselves. */
  matches: Set<string> | null;
  problems: Set<string>;
  /** Parts added or changed in the draft: `new` or `changed`. */
  changed: Map<string, 'new' | 'changed'>;
  show: Set<EdgeKind>;
  allLines: boolean;
  compact: boolean;
}) {
  const { t } = useT();
  const root = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [folds, setFolds] = useState<Record<string, boolean>>({});

  const lanes = useMemo(() => {
    const out: { lane: Lane; groups: Group[]; count: number }[] = [];
    for (const lane of LANES) {
      const inLane = graph.nodes.filter((n) => n.lane === lane && visible(n));
      if (inLane.length === 0) continue;
      const groups: Group[] = [];
      if (lane === 'process' || lane === 'view') {
        for (const kind of DIRECTIVE_LISTS) {
          const parts = inLane.filter((n) => n.kind === kind).sort((a, b) => a.label.localeCompare(b.label));
          if (parts.length === 0) continue;
          const id = `group:${kind}`;
          const forced = parts.some((p) => p.id === selected) || (matches != null && parts.some((p) => matches.has(p.id)));
          const folded = forced ? false : folds[id] ?? parts.length > OPEN_UP_TO;
          groups.push({ id, kind, rows: parts.map((node) => ({ node, depth: 0, children: 0 })), folded });
        }
      } else if (lane === 'value') {
        groups.push({ id: 'group:values', kind: 'values', rows: inLane.sort((a, b) => a.label.localeCompare(b.label)).map((node) => ({ node, depth: 0, children: 0 })), folded: false });
      } else {
        // Each template under the one it extends; a template with children folds them away.
        const rows: Row[] = [];
        const childrenOf = (key: string) => inLane.filter((c) => c.parent === key).sort((a, b) => a.label.localeCompare(b.label));
        const isOpen = (n: TaxNode) => {
          if (!folds[`tree:${n.id}`]) return true;
          // Opened by itself when what is chosen or found is under it.
          const under = new Set<string>();
          const walk = (k: string) => childrenOf(k).forEach((c) => { under.add(c.id); walk(c.key); });
          walk(n.key);
          return (selected != null && under.has(selected)) || (matches != null && [...under].some((u) => matches.has(u)));
        };
        const place = (n: TaxNode, depth: number) => {
          const kids = childrenOf(n.key);
          const open = kids.length > 0 && isOpen(n);
          rows.push({ node: n, depth, children: kids.length, open });
          if (open) kids.forEach((c) => place(c, depth + 1));
        };
        inLane.filter((n) => !n.parent || !inLane.some((p) => p.key === n.parent))
          .sort((a, b) => a.label.localeCompare(b.label)).forEach((n) => place(n, 0));
        groups.push({ id: `group:${lane}`, kind: 'tree', rows, folded: false });
      }
      out.push({ lane, groups, count: inLane.length });
    }
    return out;
  }, [graph, visible, folds, selected, matches]);

  /** Where an id is drawn: its own box, or what it is folded into. */
  const anchor = useCallback((id: string): Element | null => {
    const element = root.current;
    if (!element) return null;
    const own = element.querySelector(`[data-node="${CSS.escape(id)}"]`);
    if (own) return own;
    const node = graph.byId.get(id);
    if (!node) return null;
    if (node.kind === 'template') {
      for (let at = node.parent ? graph.byId.get(`template:${node.parent}`) : undefined; at; at = at.parent ? graph.byId.get(`template:${at.parent}`) : undefined) {
        const box = element.querySelector(`[data-node="${CSS.escape(at.id)}"]`);
        if (box) return box;
      }
      return null;
    }
    return element.querySelector(`[data-group="group:${node.kind}"]`);
  }, [graph]);

  const wires = useMemo(() => {
    const out: Wire[] = [];
    const drawn = graph.edges.filter((e) => show.has(e.kind));
    if (allLines) {
      for (const e of drawn) {
        if (e.from === selected || e.to === selected) continue;
        const a = graph.byId.get(e.from); const b = graph.byId.get(e.to);
        if (a && b && visible(a) && visible(b)) out.push({ from: e.from, to: e.to, kind: e.kind });
      }
    }
    if (hover && hover !== selected) {
      for (const e of drawn) if (e.from === hover || e.to === hover) out.push({ from: e.from, to: e.to, kind: e.kind });
    }
    if (selected) {
      for (const e of drawn) if (e.from === selected || e.to === selected) out.push({ from: e.from, to: e.to, kind: e.kind, label: e.label, strong: true });
    }
    return out;
  }, [graph, show, selected, hover, allLines, visible]);

  const near = useMemo(() => {
    if (!selected) return null;
    const out = new Set<string>([selected]);
    for (const e of graph.edges) {
      if (!show.has(e.kind)) continue;
      if (e.from === selected) out.add(e.to);
      if (e.to === selected) out.add(e.from);
    }
    return out;
  }, [graph, selected, show]);

  const toggle = (id: string, folded: boolean) => setFolds((was) => ({ ...was, [id]: !folded }));
  const columns = COLUMNS.map((c) => lanes.filter((l) => c.includes(l.lane))).filter((c) => c.length > 0);
  const labelled = wires.filter((w) => w.strong).length <= LABELS_UP_TO ? wires : wires.map((w) => ({ ...w, label: undefined }));

  return (
    <div className={`tx-map${compact ? ' compact' : ''}`} ref={root} data-testid="taxonomy-map"
         style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(${compact ? 128 : 138}px, 1fr))` }}>
      {columns.map((column) => (
        <div className="tx-map-column" key={column[0].lane}>
      {column.map(({ lane, groups, count }) => (
        <section key={lane} className={`tx-map-lane tx-lane-${lane}`} data-testid={`tx-lane-${lane}`}>
          <header className="tx-map-lane-head">
            <span className={`tx-dot tx-dot-${lane}`} />
            <span>{t(`taxonomy.lane.${lane}` as MessageKey)}</span>
            <span className="tx-count">{count}</span>
          </header>
          {groups.map((g) => (
            <div key={g.id} className="tx-map-group">
              {g.kind !== 'tree' && g.kind !== 'values' && (
                <button className={`tx-map-group-head${g.folded ? '' : ' open'}`} data-group={g.id}
                        data-testid={`tx-group-${g.kind}`} aria-expanded={!g.folded}
                        onClick={() => toggle(g.id, g.folded)}>
                  <span className="tx-caret">{g.folded ? '▸' : '▾'}</span>
                  <KindIcon kind={g.kind} />
                  <span className="tx-group-name">{t(`taxonomy.kinds.${g.kind}` as MessageKey)}</span>
                  <span className="tx-count">{g.rows.length}</span>
                  {g.folded && g.rows.some((r) => problems.has(r.node.id)) && <span className="tx-flag bad" />}
                  {g.folded && g.rows.some((r) => changed.has(r.node.id)) && <span className="tx-flag changed" />}
                </button>
              )}
              {!g.folded && g.rows.map(({ node, depth, children, open = false }) => {
                const classes = ['tx-map-node', `tx-kind-${node.kind}`];
                if (node.id === selected) classes.push('on');
                else if (near && near.has(node.id)) classes.push('near');
                else if (near) classes.push('far');
                if (node.isAbstract) classes.push('abstract');
                if (problems.has(node.id)) classes.push('bad');
                if (matches && !matches.has(node.id)) classes.push('unmatched');
                const change = changed.get(node.id);
                const treeId = `tree:${node.id}`;
                return (
                  <div key={node.id} className="tx-map-row" style={{ paddingLeft: Math.min(depth, 5) * 12 }}>
                    {g.kind === 'tree' && (children > 0
                      ? <button className="tx-tree-caret" aria-expanded={open} aria-label={t(open ? 'tx.fold' : 'tx.unfold')}
                                onClick={() => setFolds((was) => ({ ...was, [treeId]: open }))}>{open ? '▾' : '▸'}</button>
                      : depth > 0 ? <span className="tx-tree-caret" /> : null)}
                    <button className={classes.join(' ')} data-node={node.id} data-testid={`tx-node-${node.id}`}
                            title={`${node.key} · ${node.pack}`}
                            onClick={() => onSelect(node.id)} onDoubleClick={() => onOpen(node.id)}
                            onMouseEnter={() => setHover(node.id)} onMouseLeave={() => setHover((h) => (h === node.id ? null : h))}>
                      <span className="tx-node-label">{node.label}</span>
                      <span className="tx-node-tags">
                        {change && <span className={`tx-change ${change}`} title={t(`tx.change.${change}` as MessageKey)} />}
                        {!open && children > 0 && <span className="tx-kids">+{children}</span>}
                      </span>
                      {!compact && <span className="tx-node-key">{node.key}</span>}
                      {!compact && node.pack !== CORE_PACKAGE && <span className="tx-pack-tag">{node.pack}</span>}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </section>
      ))}
        </div>
      ))}
      <Wires root={root} wires={labelled} anchor={anchor} watch={lanes} />
    </div>
  );
}
